<template>
  <b-modal
    ref="modal"
    hide-footer
    size="lg"
    title="Edit API Key"
    @hidden="resetForm"
  >
    <b-overlay :show="loading">
      <b-form @submit.prevent="onSubmit">
        <b-form-row>
          <b-form-group class="col-md-6" label="Name *">
            <b-form-input
              v-model="form.name"
              type="text"
              size="sm"
              required
            />
          </b-form-group>

          <b-form-group class="col-md-6" label="Environment">
            <b-form-input
              :value="environmentText"
              size="sm"
              disabled
            />
            <small class="text-muted">Environment cannot be changed</small>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-md-6" label="Status *">
            <b-form-select
              v-model="form.status"
              :options="statusOptions"
              size="sm"
              :disabled="apiKey.status === 'revoked'"
              required
            />
            <small v-if="apiKey.status === 'revoked'" class="text-danger">
              Revoked keys cannot be reactivated
            </small>
          </b-form-group>

          <b-form-group class="col-md-6" label="Rate Limit (requests/min)">
            <b-form-input
              v-model="form.rate_limit"
              type="number"
              size="sm"
              min="1"
            />
            <small class="text-muted">Leave empty for unlimited</small>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-md-6" label="Expiration Date">
            <b-form-input
              v-model="form.expires_at"
              type="datetime-local"
              size="sm"
            />
            <small class="text-muted">Leave empty for no expiration</small>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-12" label="IP Whitelist">
            <b-form-textarea
              v-model="ipWhitelistText"
              placeholder="192.168.1.1&#10;10.0.0.1"
              rows="3"
              size="sm"
            />
            <small class="text-muted">One IP per line. Leave empty to allow all IPs</small>
            <div v-if="ipValidationErrors.length > 0" class="text-danger mt-2">
              <small>
                <md-icon icon="error" class="text-danger" />
                Invalid IPs: {{ ipValidationErrors.join(', ') }}
              </small>
            </div>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-12" label="API Key (Hidden for security)">
            <b-form-input
              :value="maskedKey"
              size="sm"
              disabled
            />
            <small class="text-muted">
              The full key is hidden for security. Use "Regenerate" to create a new key.
            </small>
          </b-form-group>
        </b-form-row>

        <div class="text-right mt-3">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="hide"
            :disabled="loading"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="loading || ipValidationErrors.length > 0 || apiKey.status === 'revoked'"
          >
            <md-icon icon="save" />
            Update
          </b-button>
        </div>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
export default {
  name: 'EditApiKeyModal',
  data() {
    return {
      loading: false,
      apiKey: {},
      form: {
        name: '',
        status: 'active',
        rate_limit: null,
        expires_at: null
      },
      ipWhitelistText: '',
      statusOptions: [
        { value: 'active', text: 'Active' },
        { value: 'inactive', text: 'Inactive' }
      ]
    };
  },
  computed: {
    environmentText() {
      return this.apiKey.environment === 'sandbox' ? 'Sandbox (Testing)' : 'Live (Production)';
    },
    maskedKey() {
      if (!this.apiKey.environment) return '';
      const prefix = this.apiKey.environment === 'sandbox' ? 'sk_sandbox_' : 'sk_live_';
      return `${prefix}••••••••••••••••••••••••••••••••`;
    },
    ipWhitelistArray() {
      return this.ipWhitelistText
        .split('\n')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);
    },
    ipValidationErrors() {
      const errors = [];
      this.ipWhitelistArray.forEach(ip => {
        if (!this.isValidIP(ip)) {
          errors.push(ip);
        }
      });
      return errors;
    }
  },
  methods: {
    show(apiKey) {
      this.apiKey = { ...apiKey };
      this.form = {
        name: apiKey.name,
        status: apiKey.status,
        rate_limit: apiKey.rate_limit,
        expires_at: apiKey.expires_at ? this.formatDateForInput(apiKey.expires_at) : null
      };
      this.ipWhitelistText = Array.isArray(apiKey.ip_whitelist)
        ? apiKey.ip_whitelist.join('\n')
        : '';
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    resetForm() {
      this.apiKey = {};
      this.form = {
        name: '',
        status: 'active',
        rate_limit: null,
        expires_at: null
      };
      this.ipWhitelistText = '';
      this.loading = false;
    },
    formatDateForInput(dateString) {
      const date = new Date(dateString);
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      return date.toISOString().slice(0, 16);
    },
    isValidIP(ip) {
      const regex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!regex.test(ip)) return false;
      return ip.split('.').every(num => parseInt(num) >= 0 && parseInt(num) <= 255);
    },
    async onSubmit() {
      this.loading = true;

      const payload = {
        name: this.form.name,
        status: this.form.status,
        rate_limit: this.form.rate_limit || null,
        ip_whitelist: this.ipWhitelistArray.length > 0 ? this.ipWhitelistArray : null,
        expires_at: this.form.expires_at || null
      };

      try {
        await this.$root.api.put(`/suppliers/api-keys/${this.apiKey.id}`, payload);

        this.$root.showAlert('API key updated successfully', { variant: 'success' });
        this.$emit('updated');
        this.hide();
      } catch (error) {
        if (error.response?.status === 422) {
          this.$root.showAlert(
            error.response.data.message || 'Validation error',
            { variant: 'danger' }
          );
        } else {
          this.$root.handleRequestError(error);
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.form-control:disabled {
  background-color: #e9ecef;
  opacity: 0.7;
}
</style>
